/**
 * Owner-facing suspicion predicate — mirrors `backend/services/classification_suspicion.py` (Story 7.8).
 * Shared examples: `src/__fixtures__/classification_suspicion_cases.json`.
 */

import type { ClassificationSource, ConfidenceBand, WorkItem } from '../api/workItems';

const CLASSIFICATION_SOURCES = new Set<string>([
    'heuristic',
    'ai_enrichment',
    'user_override',
    'bulk_user_approved',
    'migration',
    'system_default',
    'unknown',
]);

const CONFIDENCE_BANDS = new Set<string>(['none', 'zero', 'low', 'medium', 'high']);

export function normalizeClassificationSource(raw: WorkItem['classification_source']): ClassificationSource {
    const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (!s || !CLASSIFICATION_SOURCES.has(s)) {
        return 'unknown';
    }
    return s as ClassificationSource;
}

export function normalizeDomainKey(domain: WorkItem['domain']): string {
    return String(domain ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
}

export function isSpecificDomain(domain: WorkItem['domain']): boolean {
    const key = normalizeDomainKey(domain);
    return key.length > 0 && key !== 'general';
}

export function normalizeEditedFields(raw: unknown): string[] {
    if (raw == null) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x)).filter((x) => x.trim().length > 0);
    }
    if (typeof raw === 'string' && raw.trim()) {
        return [raw.trim()];
    }
    return [];
}

/** Canonical Story 7.5 predicate — duplicated minimally for pure client-side checks. */
export function domainOverrideActive(meta: WorkItem['meta']): boolean {
    if (!meta || typeof meta !== 'object') {
        return false;
    }
    const uo = (meta as Record<string, unknown>).user_overrides;
    if (!uo || typeof uo !== 'object') {
        return false;
    }
    const fields = normalizeEditedFields((uo as Record<string, unknown>).edited_fields);
    return new Set(fields).has('domain');
}

export function normalizeStoreConfidenceNumeric(raw: number | null | undefined): {
    value: number | null;
    invalidNegative: boolean;
} {
    if (raw == null || Number.isNaN(raw)) {
        return { value: null, invalidNegative: false };
    }
    if (!Number.isFinite(raw)) {
        return { value: null, invalidNegative: false };
    }
    if (raw < 0) {
        return { value: null, invalidNegative: true };
    }
    const bounded = raw > 1 ? 1 : raw;
    return { value: bounded, invalidNegative: false };
}

export function deriveConfidenceBand(confidenceNumeric: number | null): ConfidenceBand {
    if (confidenceNumeric == null) {
        return 'none';
    }
    if (confidenceNumeric === 0) {
        return 'zero';
    }
    if (confidenceNumeric < 0) {
        return 'none';
    }
    if (confidenceNumeric < 0.5) {
        return 'low';
    }
    if (confidenceNumeric < 0.8) {
        return 'medium';
    }
    return 'high';
}

export function resolveStoredConfidenceNumeric(
    confidenceNumeric: WorkItem['confidence_numeric'],
    confidenceScoreLegacy: WorkItem['confidence_score']
): { numeric: number | null; invalidNegative: boolean } {
    let invalidNegative = false;

    if (confidenceNumeric != null && Number.isFinite(confidenceNumeric)) {
        const { value, invalidNegative: inv } = normalizeStoreConfidenceNumeric(confidenceNumeric);
        invalidNegative = invalidNegative || inv;
        if (value != null) {
            return { numeric: value, invalidNegative };
        }
    }

    if (confidenceScoreLegacy != null && confidenceScoreLegacy !== undefined) {
        const legacyInt = Number(confidenceScoreLegacy);
        if (Number.isFinite(legacyInt)) {
            const { value, invalidNegative: inv2 } = normalizeStoreConfidenceNumeric(legacyInt / 100);
            invalidNegative = invalidNegative || inv2;
            return { numeric: value, invalidNegative };
        }
    }

    return { numeric: null, invalidNegative };
}

function legacyIntNonPositive(confidenceScoreLegacy: WorkItem['confidence_score']): boolean {
    if (confidenceScoreLegacy == null || confidenceScoreLegacy === undefined) {
        return false;
    }
    const n = Number(confidenceScoreLegacy);
    return Number.isFinite(n) && n <= 0;
}

export type SuspicionInputs = Pick<
    WorkItem,
    | 'domain'
    | 'classification_source'
    | 'confidence_numeric'
    | 'confidence_score'
    | 'confidence_band'
    | 'evidence_summary'
    | 'meta'
>;

export function isSuspiciousClassificationInputs(inputs: SuspicionInputs): boolean {
    if (!isSpecificDomain(inputs.domain)) {
        return false;
    }
    if (domainOverrideActive(inputs.meta)) {
        return false;
    }

    const normSource = normalizeClassificationSource(inputs.classification_source);
    const unknownSource = normSource === 'unknown';

    const { numeric: effectiveNumeric, invalidNegative } = resolveStoredConfidenceNumeric(
        inputs.confidence_numeric,
        inputs.confidence_score
    );
    const bandFromNumeric = deriveConfidenceBand(effectiveNumeric);

    const storedBandRaw = inputs.confidence_band;
    const storedBand =
        typeof storedBandRaw === 'string' ? storedBandRaw.trim().toLowerCase() : '';
    const effectiveBand: ConfidenceBand = CONFIDENCE_BANDS.has(storedBand)
        ? (storedBand as ConfidenceBand)
        : bandFromNumeric;

    const weakBand = effectiveBand === 'none' || effectiveBand === 'zero' || effectiveBand === 'low';
    const evidenceMissing = !String(inputs.evidence_summary ?? '').trim();
    const weakEvidence = evidenceMissing || weakBand;

    const badConfidence =
        invalidNegative ||
        effectiveNumeric == null ||
        effectiveNumeric <= 0 ||
        legacyIntNonPositive(inputs.confidence_score);

    return unknownSource || badConfidence || weakEvidence;
}

export function isSuspiciousWorkItem(item: WorkItem): boolean {
    return isSuspiciousClassificationInputs(item);
}

/** Prefer server-computed `suspicious_classification` when present (deploy parity); else local predicate. */
export function isSuspiciousWorkItemDisplay(item: WorkItem): boolean {
    return item.suspicious_classification ?? isSuspiciousWorkItem(item);
}
